/** Sentry truncates tag values at 200 characters. */
const MAX_TAG_VALUE_LENGTH = 200;

type TagSetter = (key: string, value: string) => void;

type HighEntropyGetter = (hints: string[]) => Promise<unknown>;

interface UserAgentBrand {
  brand: string;
  version: string;
}

function readProperty(target: object, key: string): unknown {
  return Reflect.get(target, key);
}

function isObject(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}

function isHighEntropyGetter(value: unknown): value is HighEntropyGetter {
  return typeof value === 'function';
}

function isBrandList(value: unknown): value is UserAgentBrand[] {
  return (
    Array.isArray(value) &&
    value.every(
      entry =>
        isObject(entry) &&
        typeof readProperty(entry, 'brand') === 'string' &&
        typeof readProperty(entry, 'version') === 'string',
    )
  );
}

/**
 * True when the page runs inside a WebView2 host. `chrome.webview` is the host
 * bridge object, present only there, so it separates an application that embeds
 * a browser engine from a real Edge install reporting the same user agent.
 */
export function isWebView2Host(): boolean {
  const chrome = readProperty(window, 'chrome');
  return isObject(chrome) && 'webview' in chrome;
}

/** True when the session is driven by WebDriver or the DevTools protocol. */
export function isAutomatedSession(): boolean {
  return navigator.webdriver === true;
}

/** Renders a user-agent brand list into a single, tag-length-safe value. */
export function formatBrands(brands: UserAgentBrand[]): string {
  return brands
    .map(entry => `${entry.brand} ${entry.version}`)
    .join(', ')
    .slice(0, MAX_TAG_VALUE_LENGTH);
}

/**
 * Tags the client environment so errors reported from page-injected code can be
 * attributed to the software injecting it. Sentry does not index `extra`, so
 * anything we need to break an issue down by has to be a tag.
 *
 * `client.ua_brands` is set twice when the browser supports it: once from the
 * low-entropy brand list, then again from `fullVersionList`, which is the only
 * place an embedding runtime names itself (e.g. "Microsoft Edge WebView2").
 * The second write is asynchronous, so events sent in the first moments after
 * load carry the low-entropy value.
 * @param setTag - Sets a tag on Sentry's global scope.
 */
export function applyClientFingerprintTags(setTag: TagSetter): void {
  setTag('client.webview2', String(isWebView2Host()));
  setTag('client.webdriver', String(isAutomatedSession()));

  const userAgentData = readProperty(navigator, 'userAgentData');
  if (!isObject(userAgentData)) {
    return;
  }

  const brands = readProperty(userAgentData, 'brands');
  if (isBrandList(brands)) {
    setTag('client.ua_brands', formatBrands(brands));
  }

  const getHighEntropyValues = readProperty(
    userAgentData,
    'getHighEntropyValues',
  );
  if (!isHighEntropyGetter(getHighEntropyValues)) {
    return;
  }

  getHighEntropyValues
    .call(userAgentData, ['fullVersionList'])
    .then(values => {
      if (!isObject(values)) {
        return;
      }
      const fullVersionList = readProperty(values, 'fullVersionList');
      if (isBrandList(fullVersionList)) {
        setTag('client.ua_brands', formatBrands(fullVersionList));
      }
    })
    .catch(() => {
      // Rejects when the permission policy denies high-entropy hints; the
      // low-entropy brands tag above is the fallback.
    });
}
